// Segment first, as the legacy entry did: it was imported after `await contextFactory()` (which waits
// for Optimizely), and every visit that left before that sent no page or list events (−14% on the
// cutover).
import '@adv-mt/mt-segment-wrapper'
import './helpers/vendors/index.js'
import './styles/index.js'

import type {ComponentType, ReactElement} from 'react'
import {useEffect} from 'react'
import {type Root, hydrateRoot} from 'react-dom/client'
import {type RouteObject, createBrowserRouter, matchRoutes, RouterProvider} from 'react-router-dom'

import {datadogRum} from '@datadog/browser-rum'
import {ErrorBoundary, reactPlugin} from '@datadog/browser-rum-react'

import {PdeContext} from '@s-ui/pde'
import Context from '@s-ui/react-context'
import {HeadProvider} from '@s-ui/react-head'

import createClientContextFactoryParams from './contextFactory/createClientContextFactoryParams.js'
import contextFactory from './contextFactory/index.js'
import {routes} from './routes.tsx'

const MINIMAL_ERROR_PAGE = '<h1>500 - Internal Server Error</h1>'

// Initialize DataDog RUM
process.env.NODE_ENV === 'production' &&
  datadogRum?.init({
    applicationId: 'bbf57996-c7d6-4e14-87fd-de415cf453cc',
    clientToken: 'pub0904350e7d8faabbcc3676bd9302727d',
    site: 'datadoghq.com',
    service: 'coches-web-app',
    env: process.env.NODE_ENV,
    sessionSampleRate: 1,
    sessionReplaySampleRate: 15,
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin({router: false})],
    beforeSend: event => {
      if (event.type === 'error') {
        const source = event.error?.source || ''
        const type = event.error?.type || ''

        if (source === 'report' && type === 'worker-src') {
          return false
        }
      }
      return true
    }
  })

// Initialize context and hydrate
void contextFactory(createClientContextFactoryParams()).then(async context => {
  const {domain} = context

  // Helper function to wrap a loader with context injection
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const wrapLoaderWithContext = (loader: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return async (args: any) => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[RR7 Entry Client] Calling loader with context:', {
          hasContext: Boolean(context),
          hasDomain: Boolean(context?.domain),
          hasI18n: Boolean(context?.i18n)
        })
      }
      return loader({...args, context})
    }
  }

  // Helper function to process children routes recursively
  const processChildren = (children: RouteObject[] | undefined): RouteObject[] | undefined => {
    if (children === undefined) return children

    return children.map(childRoute => {
      // Handle lazy routes
      if (childRoute.lazy !== undefined) {
        return {
          ...childRoute,
          lazy: async () => {
            const lazyFn = childRoute.lazy
            if (lazyFn === undefined) {
              throw new Error('Lazy function is undefined')
            }
            const module = await lazyFn()
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.log('[RR7 Entry Client] Loaded lazy route, has loader:', Boolean(module.loader))
            }
            return {
              ...module,
              loader: module.loader !== undefined ? wrapLoaderWithContext(module.loader) : undefined
            }
          }
        }
      }

      // Handle static routes with loader
      if (childRoute.loader !== undefined) {
        const wrappedRoute = {
          ...childRoute,
          loader: wrapLoaderWithContext(childRoute.loader)
        }

        // Recursively process children if they exist
        if (childRoute.children !== undefined) {
          wrappedRoute.children = processChildren(childRoute.children)
        }

        return wrappedRoute
      }

      // Handle routes with children but no loader
      if (childRoute.children !== undefined) {
        return {
          ...childRoute,
          children: processChildren(childRoute.children)
        }
      }

      return childRoute
    })
  }

  // Wrap routes to inject context into loaders (both lazy and static)
  const routesWithContext: RouteObject[] = routes.map(route => {
    // Handle lazy routes at root level
    if (route.lazy !== undefined) {
      return {
        ...route,
        lazy: async () => {
          const lazyFn = route.lazy
          if (lazyFn === undefined) {
            throw new Error('Lazy function is undefined')
          }
          const module = await lazyFn()
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.log('[RR7 Entry Client] Loaded root lazy route, has loader:', Boolean(module.loader))
          }
          return {
            ...module,
            loader: module.loader !== undefined ? wrapLoaderWithContext(module.loader) : undefined
          }
        }
      }
    }

    // Handle routes with children (like Root component)
    if (route.children !== undefined) {
      return {
        ...route,
        children: processChildren(route.children)
      }
    }

    // Handle static routes with loader at root level
    if (route.loader !== undefined) {
      return {
        ...route,
        loader: wrapLoaderWithContext(route.loader)
      }
    }

    return route
  })

  // Pre-resolve every lazy module that matches the current URL BEFORE creating the router.
  // Without this, RR7 boots the router with `lazy:` placeholders that resolve async; React
  // then sees the SSR-rendered tree mismatch the (still-loading) client tree and discards
  // the SSR DOM, re-rendering from scratch — which can flash a duplicated layout shell.
  // Resolving here keeps hydration fully synchronous and lossless.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const matchedForHydration = matchRoutes(routesWithContext as any, window.location.pathname) ?? []
  await Promise.all(
    matchedForHydration.map(async match => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const route = match.route as any
      if (typeof route.lazy !== 'function') return
      const resolved = await route.lazy()
      // Splice resolved module into the route in-place so RR7 sees a fully-loaded route.
      Object.assign(route, resolved)
      delete route.lazy
    })
  )

  // Create browser router with hydration data
  // NOTE: Do NOT set basename here - we serve from root (/)
  // The base: '/assets/' in vite.config is only for built JS/CSS files
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const router = createBrowserRouter(routesWithContext, {
    basename: '/',
    hydrationData: (window as any).__staticRouterHydrationData
  })

  // Listen to navigation changes for domain config
  router.subscribe(state => {
    const hasLocation = state.location !== undefined && state.location !== null
    if (hasLocation) {
      const url = `${window.location.protocol}//${window.location.host}${state.location.pathname}`
      const config = domain.get('config')
      const pageUrlHeader = config.get('REQUEST_HEADERS').URL
      domain.config(pageUrlHeader, url)
    }
  })

  // Mirrors `sendErrorPage` in entry.server.tsx: when the tree is unrenderable the user gets the
  // branded 500, never a stack trace. The page is a whole document (its CSS lives in <head>), so it
  // cannot be rendered into #root — the document is replaced instead. Loaded on demand so the happy
  // path never pays for its ~18KB.
  const BrandedErrorPage = (): null => {
    useEffect(() => {
      const importErrorPage = context.isMotos
        ? async () => await import('../500-motos.html?raw')
        : async () => await import('../500-coches.html?raw')

      const replaceDocument = (html: string): void => {
        document.open()
        document.write(html)
        document.close()
      }

      importErrorPage()
        .then(({default: html}) => replaceDocument(html))
        .catch(() => replaceDocument(MINIMAL_ERROR_PAGE))
    }, [])

    return null
  }

  // Wrapper for error boundary or hydration overlay
  // In dev mode, just use Fragment for simplicity
  const Wrapper =
    process.env.NODE_ENV === 'production'
      ? ErrorBoundary
      : ({children}: {children: ReactElement; fallback: ComponentType}) => children

  // Start mocker in development (if not Cypress)
  // TEMPORARY: Disabled MSW to test with real preproduction data
  // if (process.env.STAGE === 'development' && Boolean(window.Cypress) === false) {
  //   const mocker = await import('@adv-mt/mt-mocks/index.js').then(pkg => pkg.getMocker())
  //   mocker.start({onUnhandledRequest: 'bypass'})
  // }

  // Context and PdeContext are provided here at the entry level
  // Route-specific providers (Advertising, Authentication, LoginModal) are in root.tsx
  // so they are accessible to all routes rendered through <Outlet />

  const app = (
    <Wrapper fallback={BrandedErrorPage}>
      <Context.Provider value={context}>
        <PdeContext.Provider value={{pde: context.pde}}>
          <HeadProvider>
            <RouterProvider router={router} />
          </HeadProvider>
        </PdeContext.Provider>
      </Context.Provider>
    </Wrapper>
  )

  // Always use hydrateRoot for SSR hydration (both dev and production)
  // Using createRoot in dev would cause double rendering since SSR already generated HTML
  const rootElement = document.getElementById('root')
  if (rootElement === null) {
    throw new Error('Root element not found')
  }

  // Cache the Root instance on `window` so HMR re-executions of this module
  // (triggered by `import.meta.hot.accept()` below) reuse the existing React
  // tree via `root.render(...)` instead of calling `hydrateRoot(...)` a
  // second time. A second `hydrateRoot` on an already-mounted container
  // does NOT reuse the previous root; React mounts a NEW tree alongside the
  // existing one — visible in the DOM as two copies of the app inside
  // `#root` (two `<footer>`, two ad slots, two page containers, etc.).
  const globalRoot = window as unknown as {__mtHmrRoot?: Root}
  if (globalRoot.__mtHmrRoot === undefined) {
    globalRoot.__mtHmrRoot = hydrateRoot(rootElement, app)
  } else {
    globalRoot.__mtHmrRoot.render(app)
  }
})

// Vite HMR
if (import.meta.hot !== undefined) {
  import.meta.hot.accept()
}
